import React, { useEffect, useState, useRef } from 'react';
import { FiX, FiRotateCw, FiCrop, FiRefreshCw, FiCheck } from 'react-icons/fi';

interface FreeformImageCropModalProps {
  isOpen: boolean;
  imageSrc: string | null;
  isSubmitting?: boolean;
  title?: string;
  onCancel: () => void;
  onConfirm: (cropAreaPixels: {
    x: number;
    y: number;
    width: number;
    height: number;
  }) => void | Promise<void>;
}

export const FreeformImageCropModal: React.FC<FreeformImageCropModalProps> = ({
  isOpen,
  imageSrc,
  isSubmitting = false,
  title = 'Crop Image',
  onCancel,
  onConfirm,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(
    null,
  );
  const [rotation, setRotation] = useState(0);

  // Crop rectangle in percentages (0-100) relative to container
  const [cropRect, setCropRect] = useState({ x: 5, y: 5, width: 90, height: 90 });
  const [activeHandle, setActiveHandle] = useState<string | null>(null);
  const dragStartRef = useRef<{
    mouseX: number;
    mouseY: number;
    initialRect: typeof cropRect;
  } | null>(null);

  useEffect(() => {
    if (!isOpen || !imageSrc) return;
    const img = new Image();
    img.onload = () => {
      setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
      setCropRect({ x: 5, y: 5, width: 90, height: 90 });
      setRotation(0);
    };
    img.src = imageSrc;
  }, [imageSrc, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSubmitting) onCancel();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [isOpen, isSubmitting, onCancel]);

  if (!isOpen || !imageSrc) return null;

  // Pointer move & up handlers for handle dragging
  const handlePointerDown = (handle: string, e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    setActiveHandle(handle);
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      initialRect: { ...cropRect },
    };
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!activeHandle || !dragStartRef.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const deltaXPercent = ((e.clientX - dragStartRef.current.mouseX) / rect.width) * 100;
    const deltaYPercent = ((e.clientY - dragStartRef.current.mouseY) / rect.height) * 100;

    const init = dragStartRef.current.initialRect;
    let newX = init.x;
    let newY = init.y;
    let newW = init.width;
    let newH = init.height;

    const minSize = 5; // Minimum size in %

    if (activeHandle === 'move') {
      newX = Math.max(0, Math.min(100 - init.width, init.x + deltaXPercent));
      newY = Math.max(0, Math.min(100 - init.height, init.y + deltaYPercent));
    } else {
      if (activeHandle.includes('left')) {
        const potentialW = init.width - deltaXPercent;
        if (potentialW >= minSize) {
          newX = Math.max(0, init.x + deltaXPercent);
          newW = potentialW;
        }
      }
      if (activeHandle.includes('right')) {
        newW = Math.max(minSize, Math.min(100 - init.x, init.width + deltaXPercent));
      }
      if (activeHandle.includes('top')) {
        const potentialH = init.height - deltaYPercent;
        if (potentialH >= minSize) {
          newY = Math.max(0, init.y + deltaYPercent);
          newH = potentialH;
        }
      }
      if (activeHandle.includes('bottom')) {
        newH = Math.max(minSize, Math.min(100 - init.y, init.height + deltaYPercent));
      }
    }

    setCropRect({
      x: Math.max(0, Math.min(100 - minSize, newX)),
      y: Math.max(0, Math.min(100 - minSize, newY)),
      width: Math.max(minSize, Math.min(100 - newX, newW)),
      height: Math.max(minSize, Math.min(100 - newY, newH)),
    });
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (activeHandle) {
      try {
        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      } catch {
        // pointer capture release ignored
      }
      setActiveHandle(null);
      dragStartRef.current = null;
    }
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleReset = () => {
    setCropRect({ x: 0, y: 0, width: 100, height: 100 });
    setRotation(0);
  };

  const handleApply = () => {
    if (!imageDimensions) return;
    const pixelArea = {
      x: (cropRect.x / 100) * imageDimensions.width,
      y: (cropRect.y / 100) * imageDimensions.height,
      width: (cropRect.width / 100) * imageDimensions.width,
      height: (cropRect.height / 100) * imageDimensions.height,
    };
    void onConfirm(pixelArea);
  };

  return (
    <div className="fixed inset-0 z-[350] bg-black/90 backdrop-blur-md p-3 md:p-6 flex items-center justify-center animate-in fade-in duration-200">
      <div className="w-full max-w-3xl rounded-2xl border border-[#3A3A3C] bg-[#1C1C1E] overflow-hidden shadow-2xl flex flex-col max-h-[92vh]">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2C2C2E]">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-[#1784FF]/15 text-[#1784FF]">
              <FiCrop size={18} />
            </div>
            <div>
              <h3 className="text-white text-base md:text-lg font-bold">{title}</h3>
              <p className="text-zinc-400 text-xs">
                Drag corner brackets or side handles to crop image in any custom shape
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="p-2 rounded-full text-zinc-400 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-50 cursor-pointer"
            aria-label="Close crop modal"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Interactive Cropper Area */}
        <div className="p-4 md:p-6 flex-1 flex flex-col items-center justify-center min-h-0 bg-[#0F0F11]">
          <div
            ref={containerRef}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            className="relative w-full h-[320px] sm:h-[400px] md:h-[450px] bg-black rounded-xl overflow-hidden select-none border border-[#2C2C2E] flex items-center justify-center"
          >
            {/* Base Image */}
            <img
              src={imageSrc}
              alt="Crop target"
              className="w-full h-full object-contain pointer-events-none transition-transform duration-200"
              style={{ transform: `rotate(${rotation}deg)` }}
            />

            {/* Darkened Overlay around crop box */}
            {/* Top Overlay */}
            <div
              className="absolute left-0 right-0 top-0 bg-black/65 pointer-events-none"
              style={{ height: `${cropRect.y}%` }}
            />
            {/* Bottom Overlay */}
            <div
              className="absolute left-0 right-0 bottom-0 bg-black/65 pointer-events-none"
              style={{ height: `${100 - (cropRect.y + cropRect.height)}%` }}
            />
            {/* Left Overlay */}
            <div
              className="absolute left-0 bg-black/65 pointer-events-none"
              style={{
                top: `${cropRect.y}%`,
                height: `${cropRect.height}%`,
                width: `${cropRect.x}%`,
              }}
            />
            {/* Right Overlay */}
            <div
              className="absolute right-0 bg-black/65 pointer-events-none"
              style={{
                top: `${cropRect.y}%`,
                height: `${cropRect.height}%`,
                width: `${100 - (cropRect.x + cropRect.width)}%`,
              }}
            />

            {/* WhatsApp-Style Interactive Crop Selection Box */}
            <div
              onPointerDown={(e) => handlePointerDown('move', e)}
              className="absolute border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.5)] cursor-move group/cropbox"
              style={{
                left: `${cropRect.x}%`,
                top: `${cropRect.y}%`,
                width: `${cropRect.width}%`,
                height: `${cropRect.height}%`,
              }}
            >
              {/* WhatsApp Grid lines inside crop box */}
              <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 pointer-events-none opacity-40 group-hover/cropbox:opacity-70 transition-opacity">
                <div className="border-r border-b border-white/60" />
                <div className="border-r border-b border-white/60" />
                <div className="border-b border-white/60" />
                <div className="border-r border-b border-white/60" />
                <div className="border-r border-b border-white/60" />
                <div className="border-b border-white/60" />
                <div className="border-r border-white/60" />
                <div className="border-r border-white/60" />
                <div />
              </div>

              {/* ── WhatsApp Corner Bracket Handles ────────────────────── */}
              {/* Top-Left Corner */}
              <div
                onPointerDown={(e) => handlePointerDown('top-left', e)}
                className="absolute -top-1.5 -left-1.5 w-6 h-6 border-t-4 border-l-4 border-white cursor-nwse-resize z-20 hover:scale-125 transition-transform"
              />
              {/* Top-Right Corner */}
              <div
                onPointerDown={(e) => handlePointerDown('top-right', e)}
                className="absolute -top-1.5 -right-1.5 w-6 h-6 border-t-4 border-r-4 border-white cursor-nesw-resize z-20 hover:scale-125 transition-transform"
              />
              {/* Bottom-Left Corner */}
              <div
                onPointerDown={(e) => handlePointerDown('bottom-left', e)}
                className="absolute -bottom-1.5 -left-1.5 w-6 h-6 border-b-4 border-l-4 border-white cursor-nesw-resize z-20 hover:scale-125 transition-transform"
              />
              {/* Bottom-Right Corner */}
              <div
                onPointerDown={(e) => handlePointerDown('bottom-right', e)}
                className="absolute -bottom-1.5 -right-1.5 w-6 h-6 border-b-4 border-r-4 border-white cursor-nwse-resize z-20 hover:scale-125 transition-transform"
              />

              {/* ── WhatsApp Side Edge Handles ────────────────────────── */}
              {/* Top Edge */}
              <div
                onPointerDown={(e) => handlePointerDown('top', e)}
                className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-8 h-2 bg-white rounded-full cursor-ns-resize z-20 shadow-md hover:scale-110 transition-transform"
              />
              {/* Bottom Edge */}
              <div
                onPointerDown={(e) => handlePointerDown('bottom', e)}
                className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-8 h-2 bg-white rounded-full cursor-ns-resize z-20 shadow-md hover:scale-110 transition-transform"
              />
              {/* Left Edge */}
              <div
                onPointerDown={(e) => handlePointerDown('left', e)}
                className="absolute -left-1.5 top-1/2 -translate-y-1/2 w-2 h-8 bg-white rounded-full cursor-ew-resize z-20 shadow-md hover:scale-110 transition-transform"
              />
              {/* Right Edge */}
              <div
                onPointerDown={(e) => handlePointerDown('right', e)}
                className="absolute -right-1.5 top-1/2 -translate-y-1/2 w-2 h-8 bg-white rounded-full cursor-ew-resize z-20 shadow-md hover:scale-110 transition-transform"
              />
            </div>
          </div>

          {/* WhatsApp Toolbar Controls */}
          <div className="mt-4 flex items-center justify-between w-full px-2">
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#2C2C2E] hover:bg-[#3A3A3C] text-zinc-300 hover:text-white text-xs font-semibold transition-all cursor-pointer"
              title="Reset crop selection"
            >
              <FiRefreshCw size={14} />
              <span>Reset</span>
            </button>

            <button
              type="button"
              onClick={handleRotate}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#2C2C2E] hover:bg-[#3A3A3C] text-zinc-300 hover:text-white text-xs font-semibold transition-all cursor-pointer"
              title="Rotate image 90 degrees"
            >
              <FiRotateCw size={14} className="text-[#1784FF]" />
              <span>Rotate</span>
            </button>
          </div>
        </div>

        {/* Modal Footer Buttons */}
        <div className="px-5 py-3.5 border-t border-[#2C2C2E] flex items-center justify-end gap-3 bg-[#161618]">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 rounded-full border border-[#3A3A3C] text-zinc-300 hover:text-white hover:bg-white/5 text-xs font-semibold transition-colors disabled:opacity-50 cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isSubmitting}
            onClick={handleApply}
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-[#1784FF] hover:bg-[#1784FF]/80 text-white font-bold text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md cursor-pointer"
          >
            <FiCheck size={16} />
            <span>{isSubmitting ? 'Applying Crop...' : 'Apply Crop'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
